import { reportOops } from './oops';
import { meter, meterRoom } from './lessonMeter';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SlimeLook, TeacherSlime } from '../slime/look';
import type { RoomLayout } from '../slime/room';
import { tr } from '../lang';
import type { GameMode, ModeOptions } from './modes';
import { setTeacherMe } from './teacherMe';
import { hL } from './hostLang';

// The school filter blocks any host it hasn't categorised (workers.dev, play.gakushiori.com), so the
// game is routed through the site's own address first; the others are fallbacks.
const GAME_SERVERS: string[] = (import.meta.env.VITE_GAME_SERVER_URL as string | undefined)
  ? [import.meta.env.VITE_GAME_SERVER_URL as string]
  : ['https://gakushiori.com/live', 'https://play.gakushiori.com', 'https://gakushiori-game.gakushiori.workers.dev'];

let resolved: Promise<string | null> | null = null;

// The first game server this network can reach, or null if none.
export function gameServer(): Promise<string | null> {
  if (!resolved) {
    resolved = (async () => {
      for (const base of GAME_SERVERS) {
        try {
          // Read the body: without the Cloudflare route, gakushiori.com/live/health is the site's own page
          const r = await fetch(`${base}/health`, { signal: AbortSignal.timeout(4000) });
          const body = r.ok ? await r.json().catch(() => null) : null;
          if (body?.service === 'gakushiori-game') return base;
        } catch {
          /* try the next one */
        }
      }
      return null;
    })();
    resolved.then((b) => {
      if (!b) resolved = null; // try again next time
    });
  }
  return resolved;
}

export function useGameServer(): string | null | undefined {
  const [base, setBase] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    gameServer().then((b) => live && setBase(b));
    return () => {
      live = false;
    };
  }, []);
  return base; // undefined while checking, null when unreachable
}

export interface Question {
  id: string;
  kind?: 'choice' | 'spot' | 'order' | 'sort';
  prompt: string;
  choices: string[];
  answer: number;
  fix?: { choices: string[]; answer?: number };
  words?: string[];
  explain?: string;
  tags?: string[];
  keep?: boolean; // keep the choices in this order when the game shuffles them
}

export type Mode = GameMode; // the class games (src/game/modes)

// a room's settings: these, and each game's own (its card's ModeOptions)
export interface Settings extends ModeOptions {
  questionSeconds: number;
  guessPenalty: boolean;
  showScores: 'board' | 'private';
  mode: Mode;
  roundSeconds: number;
  teams?: boolean;
  pace?: number; // meteor: how fast the sky fills
}

// the two teams, in the language in use (lang.ts)
export const TEAM_NAMES = ['Red · 紅組', 'White · 白組'];
export const teamName = (i: number) => tr(TEAM_NAMES[i]);

export type Phase = 'lobby' | 'question' | 'reveal' | 'round' | 'ended';

// ---------- the teacher pass ----------
// Opening a room needs a pass from the teacher password. "Remember this laptop" keeps it until April;
// otherwise it lives in this tab only and the server ends it after the school day.

const TEACHER_KEY = 'gk-teacher';

export function teacherPass(): string {
  try {
    return sessionStorage.getItem(TEACHER_KEY) || localStorage.getItem(TEACHER_KEY) || '';
  } catch {
    return '';
  }
}

export function forgetTeacher() {
  try {
    sessionStorage.removeItem(TEACHER_KEY);
    localStorage.removeItem(TEACHER_KEY);
    // the bank this tab kept (bankApi), so the next teacher to sign in here never sees it
    for (const k of Object.keys(sessionStorage)) if (k.startsWith('gk-bank-cache')) sessionStorage.removeItem(k);
  } catch {
    /* nothing kept */
  }
}

// Sign out everywhere (POST /teacher/signout): every other laptop's pass stops working and open rooms take their
// controls back; this laptop keeps a fresh pass, kept where the old one was.
export async function signOutEverywhere(): Promise<void> {
  const base = await gameServer();
  if (!base) throw new Error('Can’t reach the game server from this network.');
  let remember = false;
  try {
    remember = !!localStorage.getItem(TEACHER_KEY);
  } catch {
    /* nothing kept */
  }
  const r = await fetch(`${base}/teacher/signout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${teacherPass()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ remember }),
  });
  if (r.status === 401) throw new TeacherError('Sign in again.');
  if (!r.ok) throw new Error(`The game server said ${r.status}.`);
  const { pass } = await r.json();
  try {
    (remember ? localStorage : sessionStorage).setItem(TEACHER_KEY, pass);
  } catch {
    /* private window: asked again next time */
  }
}

// 'ok' when this laptop may open rooms (or the server isn't locked yet), 'ask' when it needs the password. Who is
// signed in (the teacher password's account, or a teacher it invited) goes to hostGames' setTeacherMe.
export async function teacherStatus(): Promise<'ok' | 'ask' | 'unreachable'> {
  const base = await gameServer();
  if (!base) return 'unreachable';
  try {
    const r = await fetch(`${base}/teacher`, { headers: { Authorization: `Bearer ${teacherPass()}` } });
    if (r.status === 404) return 'ok'; // a server from before the lock
    if (!r.ok) return 'unreachable';
    const { locked, ok, me } = await r.json();
    setTeacherMe(me);
    return !locked || ok ? 'ok' : 'ask';
  } catch {
    return 'unreachable';
  }
}

// ---------- an invited teacher's Google sign-in (game-server auth.ts /auth/teacher/*) ----------

const TEACHER_LOGIN_KEY = 'gk-teacher-login';

// Which sign-in buttons the game server has (Google, for now).
export async function teacherProviders(): Promise<string[]> {
  const base = await gameServer();
  if (!base) return [];
  try {
    const r = await fetch(`${base}/auth/providers`);
    return r.ok ? ((await r.json()).providers as string[]) : [];
  } catch {
    return [];
  }
}

// Leaves for the provider's sign-in page; comes back to /play/host with #login=<ticket>. invite: the link the teacher
// password's account made (the first time only).
export async function startTeacherLogin(provider: string, remember: boolean, invite?: string): Promise<'unreachable' | 'wait' | null> {
  const base = await gameServer();
  if (!base) return 'unreachable';
  try {
    const r = await fetch(`${base}/auth/teacher/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, return: `${location.origin}/play/host`, remember, ...(invite ? { invite } : {}) }),
    });
    if (r.status === 429) return 'wait';
    if (!r.ok) return 'unreachable';
    const { url, secret } = await r.json();
    try {
      sessionStorage.setItem(TEACHER_LOGIN_KEY, JSON.stringify({ secret, remember }));
    } catch {
      /* private window: the claim below can't find it, and says so */
    }
    location.assign(url);
    return null;
  } catch {
    return 'unreachable';
  }
}

export type TeacherLoginResult = { ok: true; name: string } | { ok: false; why: 'unknown' | 'invite' | 'expired' | 'taken' | 'failed' | 'unreachable' };

// Back from the provider: swap the ticket for this teacher's host pass, kept the way they chose.
export async function finishTeacherLogin(ticket: string): Promise<TeacherLoginResult> {
  const base = await gameServer();
  let kept: { secret?: string; remember?: boolean } = {};
  try {
    kept = JSON.parse(sessionStorage.getItem(TEACHER_LOGIN_KEY) || '{}');
    sessionStorage.removeItem(TEACHER_LOGIN_KEY);
  } catch {
    /* private window */
  }
  if (!ticket || ticket === 'failed' || !kept.secret) return { ok: false, why: 'failed' };
  if (!base) return { ok: false, why: 'unreachable' };
  try {
    const r = await fetch(`${base}/auth/teacher/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket, secret: kept.secret }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false, why: ['unknown', 'invite', 'expired', 'taken'].includes(data?.error) ? data.error : 'failed' };
    forgetTeacher();
    try {
      (kept.remember ? localStorage : sessionStorage).setItem(TEACHER_KEY, data.pass);
    } catch {
      /* asked again next time */
    }
    return { ok: true, name: data.name };
  } catch {
    return { ok: false, why: 'unreachable' };
  }
}

export async function teacherSignIn(code: string, remember: boolean): Promise<'ok' | 'wrong' | 'wait' | 'unreachable'> {
  const base = await gameServer();
  if (!base) return 'unreachable';
  try {
    const r = await fetch(`${base}/teacher`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, remember }),
    });
    if (r.status === 401) return 'wrong';
    if (r.status === 429) return 'wait';
    if (!r.ok) return 'unreachable';
    const { pass } = await r.json();
    forgetTeacher();
    try {
      (remember ? localStorage : sessionStorage).setItem(TEACHER_KEY, pass);
    } catch {
      /* private window: asked again next time */
    }
    return 'ok';
  } catch {
    return 'unreachable';
  }
}

export class TeacherError extends Error {}

// section: open the room for one class, so its students get a Join button instead of typing the code. A class has one
// room at a time: if its room is already open (another of this teacher's laptops opened it), this laptop joins that one
// (joined). Another teacher's game open in the class stays theirs: this says whose, and since when.
export async function createRoom(section?: string): Promise<{ code: string; hostKey: string; joined?: boolean }> {
  const base = await gameServer();
  if (!base) throw new Error('Can’t reach the game server. The school network may be blocking it.');
  const r = await fetch(`${base}/rooms`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${teacherPass()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ section }),
  });
  if (r.status === 401) throw new TeacherError('Type the teacher password again.');
  if (r.status === 409) {
    const b = (await r.json().catch(() => ({}))) as { by?: string | null; at?: number };
    const at = b.at ? new Date(b.at).toLocaleTimeString(hL('en-US', 'ja-JP'), { hour: 'numeric', minute: '2-digit' }) : '';
    throw new Error(
      hL(
        `${b.by || 'Another teacher'} has a game open in this class${at ? ` since ${at}` : ''}. It stays theirs; try again when it ends.`,
        `${b.by || 'ほかの先生'}がこのクラスでゲームを開いています${at ? `（${at}から）` : ''}。終わってからもう一度どうぞ。`,
      ),
    );
  }
  if (!r.ok) throw new Error(`Couldn't open a room (${r.status})`);
  return r.json();
}

// The teacher's own slime, saved on the game server under the teacher pass.
export async function teacherSlime(save?: TeacherSlime): Promise<TeacherSlime> {
  const base = await gameServer();
  if (!base) throw new Error('Can’t reach the game server.');
  const r = await fetch(`${base}/teacher/slime`, {
    method: save ? 'PUT' : 'GET',
    headers: { Authorization: `Bearer ${teacherPass()}`, 'Content-Type': 'application/json' },
    body: save ? JSON.stringify(save) : undefined,
  });
  if (r.status === 401) throw new TeacherError('Sign in with the teacher password again.');
  if (!r.ok) throw new Error(`Couldn't ${save ? 'save' : 'load'} your slime (${r.status})`);
  return r.json();
}

// 先生's house at the head of the lane, rearranged by the teacher's slime: saved under the teacher pass.
export async function saveHouse(room: RoomLayout): Promise<void> {
  const base = await gameServer();
  if (!base) throw new Error('Can’t reach the game server.');
  const r = await fetch(`${base}/teacher/house`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${teacherPass()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ room }),
  });
  if (r.status === 401) throw new TeacherError('Sign in with the teacher password again.');
  if (!r.ok) throw new Error(`Couldn't save your house (${r.status})`);
}

// A second teacher laptop takes the controls of a room already on the projector.
export async function joinAsControls(code: string): Promise<{ code: string; hostKey: string }> {
  const base = await gameServer();
  if (!base) throw new Error('Can’t reach the game server. The school network may be blocking it.');
  const r = await fetch(`${base}/rooms/${code}/host`, { method: 'POST', headers: { Authorization: `Bearer ${teacherPass()}` } });
  if (r.status === 401) throw new TeacherError('Set the teacher password first (see the game server README), then sign in again.');
  if (r.status === 404) throw new Error('No room with that code is open.');
  if (!r.ok) throw new Error(`Couldn't join that room (${r.status})`);
  return r.json();
}

// The rooms the teacher opened in the last 12 hours that are still open, newest first: a second laptop takes the
// controls of one with a tap (a class's room keeps its code off the projector). null: the server can't be reached.
export interface OpenRoom {
  code: string;
  section: string | null;
  name: string | null; // the class's name, or null for a room opened for any class
  at: number;
  game: string | null; // the game's name as loaded from the tray, e.g. "Review Board · Alpha"
  mode: string | null;
  here: number; // student laptops connected
  projector: boolean;
}
export async function openRooms(): Promise<OpenRoom[] | null> {
  const base = await gameServer();
  if (!base) return null;
  try {
    const r = await fetch(`${base}/teacher/rooms`, { headers: { Authorization: `Bearer ${teacherPass()}` } });
    return r.ok ? ((await r.json()).rooms ?? []) : null;
  } catch {
    return null;
  }
}

// Runs `check` now and every `ms` while someone is using this page: it's on screen and was touched (mouse, keys, a
// tap) in the last 15 minutes. A tab left open on a laptop nobody is at stops asking the game server (every ask is a
// request on Cloudflare's free daily allowance), and asks again the moment someone comes back to it. Returns a stop.
const UNUSED_MS = 15 * 60_000;
export function whileUsed(check: () => void, ms: number): () => void {
  let usedAt = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const tick = () => {
    timer = undefined;
    if (document.visibilityState !== 'visible' || Date.now() - usedAt > UNUSED_MS) return; // asleep until someone's back
    check();
    timer = setTimeout(tick, ms);
  };
  const used = () => {
    usedAt = Date.now();
    if (timer === undefined) tick();
  };
  const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'focus'] as const;
  for (const e of events) window.addEventListener(e, used, { capture: true, passive: true });
  document.addEventListener('visibilitychange', used);
  tick();
  return () => {
    clearTimeout(timer);
    for (const e of events) window.removeEventListener(e, used, { capture: true });
    document.removeEventListener('visibilitychange', used);
  };
}

// Where's my class (the host's room screen): every other place the class's slimes are on a screen right now, another
// open room or the after-school yard (code 'after-school'), and bringing them into this room. null: can't be reached.
export interface ClassPlace {
  code: string;
  kind: 'same' | 'other' | 'yard'; // a second room of this class, another class's room, the after-school yard
  name: string | null; // the other room's class
  at: number;
  students: { pid: string; name: string; look: SlimeLook | null; sticker: string | null }[];
}
export async function whereIsClass(section: string, here: string): Promise<ClassPlace[] | null> {
  const base = await gameServer();
  if (!base) return null;
  try {
    const r = await fetch(`${base}/teacher/where?${new URLSearchParams({ section, code: here })}`, { headers: { Authorization: `Bearer ${teacherPass()}` } });
    return r.ok ? (await r.json()).places : null;
  } catch {
    return null;
  }
}
export async function bringClass(section: string, from: string, to: string, pids?: string[]): Promise<number | null> {
  const base = await gameServer();
  if (!base) return null;
  try {
    const r = await fetch(`${base}/teacher/bring`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${teacherPass()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ section, from, to, pids }),
    });
    return r.ok ? (await r.json()).sent : null;
  } catch {
    return null;
  }
}

// 'open' | 'missing' for a code, or 'unreachable' when the network (often a school filter) blocks the server.
// The game the teacher opened for this class section, if one is open now, and when it was opened (at).
export async function classGame(pass: string): Promise<{ code: string; at: number } | null> {
  const base = await gameServer();
  if (!base) return null;
  try {
    const r = await fetch(`${base}/class-game`, { headers: { Authorization: `Bearer ${pass}` } });
    const d = r.ok ? await r.json() : null;
    return d?.code ? { code: d.code, at: Number(d.at) || 0 } : null;
  } catch {
    return null;
  }
}

// After school (game-server/src/after.ts): whether the class's own shop is open to meet in from home right now.
export async function afterSchool(pass: string): Promise<{ open: boolean; closes: number | null } | null> {
  const base = await gameServer();
  if (!base) return null;
  try {
    const r = await fetch(`${base}/after`, { headers: { Authorization: `Bearer ${pass}` } });
    return r.ok ? await r.json() : null;
  } catch {
    return null;
  }
}
export const afterUrl = (server: string, pass: string) => `${server.replace(/^http/, 'ws')}/after/ws?${new URLSearchParams({ pass })}`;
// my slime's own cellar out of hours (after.ts): the cabinets, cranes and 一番くじ, with nobody else down there
export const cellarUrl = (server: string, pass: string, player: string) => `${server.replace(/^http/, 'ws')}/after/cellar/ws?${new URLSearchParams({ pass, player })}`;

export async function checkRoom(code: string): Promise<'open' | 'missing' | 'unreachable'> {
  const base = await gameServer();
  if (!base) return 'unreachable';
  try {
    const r = await fetch(`${base}/rooms/${code}`);
    if (!r.ok) return 'unreachable';
    return (await r.json()).open ? 'open' : 'missing';
  } catch {
    return 'unreachable';
  }
}

type Status = 'connecting' | 'open' | 'closed';

// The network meter: with ?net in the address, every screen times a round trip to its room every 2 seconds and
// shows it in the lobby's corner (with the frame rate), to tell a slow connection from a slow computer.
export const NET_METER = typeof location !== 'undefined' && new URLSearchParams(location.search).has('net');
export const net = { rtt: null as number | null, worst: 0 };
// the heartbeat (useRoomSocket): a ping every PING_MS; a socket that hears nothing for SILENT_MS, or is still connecting
// after CONNECT_MS, is given up and a new one made
const PING_MS = 4000;
const READY_MS = 15_000; // open, but the room hasn't answered this laptop (no welcome; for a host, no state) in this long: try again
const SILENT_MS = 14_000;
const CONNECT_MS = 12_000;
// The last Slime Hop board the room sent, for a lobby that opens after it came (it comes with the welcome).
// The same for Milky Way Dash's board, with this week's course, and the cellar's 一番くじ box.
export const latest: {
  hopBoard: { top: { name: string; score: number; look: unknown }[]; record: { name: string; score: number } | null } | null;
  dashBoard: { week: string; seed: number; top: { name: string; score: number; look: unknown; k?: string }[]; record: { name: string; score: number } | null } | null;
  kujiBox: { left: Record<string, number>; total: number } | null; // the cellar's 一番くじ box, as it stands
  craneState: { bridge: unknown; tako: unknown } | null; // its 橋渡し's box and its たこ焼き's board
} = {
  hopBoard: null,
  dashBoard: null,
  kujiBox: null,
  craneState: null,
};

// One WebSocket to a room that reconnects after Wi-Fi drops. onOpen runs on every (re)connect.
export function useRoomSocket<T>(url: string | null, onOpen?: (send: (m: unknown) => void) => void) {
  const [state, setState] = useState<T | null>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const [closeCode, setCloseCode] = useState<number | null>(null);
  const [failures, setFailures] = useState(0); // connection attempts the room never answered
  const wsRef = useRef<WebSocket | null>(null);
  const onOpenRef = useRef(onOpen);
  const listeners = useRef(new Set<(m: any) => void>());
  onOpenRef.current = onOpen;

  // true when it went out on an open connection (false: nowhere to send it just now)
  const send = useCallback((m: unknown): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(m));
    return true;
  }, []);

  const listen = useCallback((fn: (m: any) => void) => {
    listeners.current.add(fn);
    return () => {
      listeners.current.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (!url) return;
    const host = new URL(url).searchParams.get('role') === 'host'; // a projector or a control laptop (no join)
    let stopped = false;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    let current: WebSocket | null = null;
    let stopNow: (() => void) | null = null; // the current socket's own clean-up
    // a fresh connection (a new room, a new pass): nothing left over from the last one's ending
    setCloseCode(null);
    setFailures(0);
    // The lesson recorder (lessonMeter.ts): a class room's laptops say how the game runs on them, every minute
    // (no names). Not the after-school yard or the cellar.
    const metered = meterRoom(url);
    if (metered) meter.attach(metered.room, metered.role);
    const reporting = metered
      ? setInterval(() => {
          const r = meter.report();
          if (r) send(r);
        }, 60_000)
      : undefined;

    const connect = () => {
      if (stopped) return;
      setStatus('connecting');
      const ws = new WebSocket(url);
      current = ws;
      wsRef.current = ws;
      let ready = false; // the room has answered this laptop (welcome, error; a host's state), not just the network
      let openedAt = 0;
      let ended = false;
      let heardAt = Date.now(); // the last thing the room said (a pong counts)
      let tickAt = Date.now();
      let pingAt = 0;
      // only the socket this hook is using now may change what the page shows (N6 in the Nov 2 review)
      const mine = () => !stopped && current === ws;

      // Once per socket, however it ends: closed by the room or the network, or given up on here.
      const end = (code: number) => {
        if (ended) return;
        ended = true;
        clearInterval(beat);
        ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
        if (stopNow === drop) stopNow = null;
        if (!mine()) return;
        setStatus('closed');
        setCloseCode(code);
        if (!ready) setFailures((f) => f + 1);
        // 4001 removed by the teacher, 4002 flooding, 4003 the same player joined from another tab (or the room is
        // on another projector laptop now), 4004 the teacher signed out everywhere (the room took its controls
        // back), 1000 room closed:
        // don't come back.
        if (code === 4001 || code === 4002 || code === 4003 || code === 4004 || code === 1000) return;
        if (metered) meter.dropped(); // the lesson recorder (lessonMeter.ts): a drop, and the clock until it's back
        // a little jitter, so a class whose Wi-Fi comes back at once doesn't all knock in the same instant
        timer = setTimeout(connect, Math.min(8000, 500 * 2 ** tries++) * (0.8 + Math.random() * 0.4));
      };
      const giveUp = () => {
        try {
          ws.close(4900, 'no answer'); // (4900: only this laptop's own word for it; it reconnects)
        } catch {
          /* already closing */
        }
        end(4900);
      };

      // The heartbeat. A connection can die without closing (Wi-Fi gone, the laptop asleep): the browser would call it
      // open for minutes while nothing arrives, and the slime would stand still on every other screen. So this laptop
      // pings every few seconds (Cloudflare answers without waking the room), and a socket that has heard nothing at
      // all for a while is given up and a new one made. A socket still connecting after a while is given up too.
      // A late tick (the tab was in the background, the laptop slept) proves nothing either way: it pings and waits.
      const beat = setInterval(() => {
        const now = Date.now();
        const late = now - tickAt > PING_MS * 2;
        tickAt = now;
        if (ws.readyState === WebSocket.CONNECTING) {
          if (!late && now - heardAt > CONNECT_MS) giveUp();
          return;
        }
        if (ws.readyState !== WebSocket.OPEN) return;
        if (late) heardAt = openedAt = now;
        else if (now - heardAt > SILENT_MS) return giveUp();
        // the network answers pings without the room (Cloudflare does): a socket that is open but whose room never
        // answered the join (Nov 2 review) is given up too
        else if (!ready && now - openedAt > READY_MS) return giveUp();
        if (!pingAt || now - pingAt > SILENT_MS) {
          pingAt = now;
          try {
            ws.send('ping');
          } catch {
            /* closing */
          }
        }
      }, PING_MS);

      // Stops this socket's heartbeat and quiets it at once, whether or not the browser ever says it has closed.
      const drop = () => {
        clearInterval(beat);
        ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      };
      stopNow = drop;

      ws.onopen = () => {
        if (!mine()) return;
        heardAt = openedAt = Date.now();
        onOpenRef.current?.(send);
      };
      ws.onmessage = (e) => {
        if (!mine()) return;
        heardAt = Date.now();
        if (e.data === 'pong') {
          if (pingAt) {
            net.rtt = Date.now() - pingAt;
            net.worst = Math.max(net.worst, net.rtt);
          }
          pingAt = 0;
          return;
        }
        let m: any;
        try {
          m = JSON.parse(e.data);
        } catch (err) {
          reportOops('A room message that wasn’t JSON', err);
          return;
        }
        if (!m || typeof m !== 'object') return;
        // the room took its controls back from every laptop (the teacher signed out everywhere): said before it closes
        // this socket, since a close can take a while to arrive
        if (m.t === 'signedout') {
          end(4004);
          try {
            ws.close();
          } catch {
            /* closing */
          }
          return;
        }
        // connected is not yet in the room. Only the room's answer to this laptop makes it 'open': a projector's or a
        // control laptop's first state, a student's welcome, or an error (a join turned away, which the page shows).
        // Anything else (other slimes' moves, rosters, boards) goes to every socket in the room, joined or not, so
        // it proves nothing about this one (the second Nov 2 review).
        if (!ready && (m.t === 'welcome' || m.t === 'error' || (host && m.t === 'state'))) {
          ready = true;
          if (metered && m.t !== 'error') {
            meter.joined();
            // a first report soon, so a short game (or a laptop that breaks early) is in the lesson's log too
            setTimeout(() => {
              const r = meter.report();
              if (r && mine()) send(r);
            }, 10_000);
          }
          tries = 0;
          setFailures(0);
          setStatus('open');
        }
        if (m.t === 'state') {
          setState(m);
          // the lesson recorder counts each game's frames apart, and the lobby's (between games)
          // (a Hop or Dash Royale is counted apart too: it runs in the lobby, on the projector's wall and the laptops)
          if (metered)
            meter.setScene(
              m.phase === 'lobby' && m.royale && !m.royale.winner
                ? `${m.royale.game === 'dash' ? 'dash' : 'hop'}-royale`
                : m.phase === 'lobby' || m.phase === 'ended'
                  ? 'lobby'
                  : String(m.mode ?? m.settings?.mode ?? 'game'),
            );
        }
        if (m.t === 'hopBoard') latest.hopBoard = m;
        if (m.t === 'dashBoard') latest.dashBoard = m;
        if (m.t === 'kujiBox') latest.kujiBox = m;
        if (m.t === 'craneState') latest.craneState = m;
        // one screen part that breaks on a message doesn't keep it from the others (N5)
        listeners.current.forEach((fn) => {
          try {
            fn(m);
          } catch (err) {
            console.error(`Room message "${m.t}" broke a listener`, err);
            reportOops(`A room message listener (${String(m.t).slice(0, 24)})`, err);
          }
        });
      };
      ws.onclose = (e) => end(e.code);
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(reporting);
      if (metered) {
        const r = meter.report();
        if (r) send(r); // what it has, on the way out
        meter.detach(metered.room);
      }
      stopNow?.();
      current?.close();
      current = null;
    };
  }, [url, send]);

  return { state, status, closeCode, failures, send, listen };
}

export function wsUrl(server: string, code: string, params: Record<string, string>) {
  return `${server.replace(/^http/, 'ws')}/rooms/${code}/ws?${new URLSearchParams(params)}`;
}

// Seconds left on the question timer, corrected for the laptop's clock.
export function useCountdown(deadline: number, serverNow: number, paused: boolean) {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!deadline) return setLeft(null);
    const skew = serverNow - Date.now();
    const tick = () => setLeft(Math.max(0, Math.ceil((deadline - (Date.now() + skew)) / 1000)));
    tick();
    if (paused) return;
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [deadline, serverNow, paused]);
  return left;
}

// Teacher-friendly paste format, one question per line:
//   take ___ account | *into | in | on # take into account = consider
// The starred choice is right. Anything after # is the explanation shown at the reveal.
export function parseQuestionText(text: string): Question[] {
  const out: Question[] = [];
  for (const line of text.split('\n')) {
    const [body, ...ex] = line.split('#');
    const parts = body.split('|').map((s) => s.trim());
    if (parts.length < 3 || !parts[0]) continue;
    const [prompt, ...rawChoices] = parts;
    const answer = rawChoices.findIndex((c) => c.startsWith('*'));
    if (answer < 0) continue;
    const choices = rawChoices.map((c) => c.replace(/^\*/, '').trim()).filter(Boolean);
    if (choices.length !== rawChoices.length) continue;
    out.push({ id: `p${out.length + 1}`, prompt, choices, answer, explain: ex.join('#').trim() || undefined });
  }
  return out;
}

export function toQuestionText(qs: Question[]): string {
  return qs
    .map(
      (q) =>
        [q.prompt, ...q.choices.map((c, i) => (i === q.answer ? `*${c}` : c))].join(' | ') +
        (q.explain ? ` # ${q.explain}` : ''),
    )
    .join('\n');
}

// Sentence correction, one per line. Mark four parts with [[ ]] and star the one with the error;
// after | come the repairs for that part, the right one starred:
//   The team was capable of [[*reach]] a [[decision]] [[before]] [[the]] deadline. | *reaching | to reach | reached # capable of + -ing
export function parseFixText(text: string): Question[] {
  const out: Question[] = [];
  for (const line of text.split('\n')) {
    const [body, ...ex] = line.split('#');
    const [sentence, ...rawFix] = body.split('|').map((x) => x.trim());
    if (!sentence) continue;
    const parts = [...sentence.matchAll(/\[\[(.+?)\]\]/g)].map((m) => m[1].trim());
    const answer = parts.findIndex((p) => p.startsWith('*'));
    if (parts.length < 2 || answer < 0 || parts.filter((p) => p.startsWith('*')).length !== 1) continue;
    const choices = parts.map((p) => p.replace(/^\*/, '').trim());
    const prompt = sentence.replace(/\[\[\s*\*?\s*(.+?)\s*\]\]/g, '[[$1]]');
    const fixAnswer = rawFix.findIndex((c) => c.startsWith('*'));
    const fixChoices = rawFix.map((c) => c.replace(/^\*/, '').trim()).filter(Boolean);
    const fix = fixAnswer >= 0 && fixChoices.length === rawFix.length && fixChoices.length >= 2 ? { choices: fixChoices, answer: fixAnswer } : undefined;
    out.push({ id: `f${out.length + 1}`, kind: 'spot', prompt, choices, answer, fix, explain: ex.join('#').trim() || undefined });
  }
  return out;
}

export function toFixText(qs: Question[]): string {
  return qs
    .map((q) => {
      let i = 0;
      const sentence = q.prompt.replace(/\[\[(.+?)\]\]/g, (_, t) => `[[${i++ === q.answer ? '*' : ''}${t}]]`);
      const fix = q.fix ? ' | ' + q.fix.choices.map((c, j) => (j === q.fix!.answer ? `*${c}` : c)).join(' | ') : '';
      return sentence + fix + (q.explain ? ` # ${q.explain}` : '');
    })
    .join('\n');
}

// Split a spot prompt into plain text and the marked parts, in order.
export function spotPieces(prompt: string): { text: string; part?: number }[] {
  const out: { text: string; part?: number }[] = [];
  let last = 0;
  let n = 0;
  for (const m of prompt.matchAll(/\[\[(.+?)\]\]/g)) {
    if (m.index! > last) out.push({ text: prompt.slice(last, m.index) });
    out.push({ text: m[1], part: n++ });
    last = m.index! + m[0].length;
  }
  if (last < prompt.length) out.push({ text: prompt.slice(last) });
  return out;
}

// Meteor phrases, one per line, tiles split by / (or by spaces when there is no /). A hint can go first, before ::
//   frame 11 :: has / the potential / to become / a community center
export function parseOrderText(text: string): Question[] {
  const out: Question[] = [];
  for (const line of text.split('\n')) {
    const [body, ...ex] = line.split('#');
    const at = body.indexOf('::');
    const hint = at >= 0 ? body.slice(0, at).trim() : '';
    const phrase = (at >= 0 ? body.slice(at + 2) : body).trim();
    if (!phrase) continue;
    const words = (phrase.includes('/') ? phrase.split('/') : phrase.split(/\s+/)).map((w) => w.trim()).filter(Boolean);
    if (words.length < 2) continue;
    out.push({ id: `m${out.length + 1}`, kind: 'order', prompt: hint, choices: [], answer: 0, words, explain: ex.join('#').trim() || undefined });
  }
  return out;
}

export function toOrderText(qs: Question[]): string {
  return qs
    .map((q) => (q.prompt ? `${q.prompt} :: ` : '') + (q.words ?? []).join(' / ') + (q.explain ? ` # ${q.explain}` : ''))
    .join('\n');
}
